// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MITs
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { generalConstants } from '@lfx/config';
import { InviteRole } from '@lfx/core/gql';
import { convertOrgAffiliationToStints } from '@lfx/core/helper';
import {
  Company,
  DefaultEmployeeObject,
  EmployeeDetailsResponse,
  EmployeeOrgAffiliation,
} from '@lfx/core/models';
import { CompanyService, ToastService, UserService } from '@lfx/core/services';
import { NgxSpinnerService } from 'ngx-spinner';
import { of, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedModule } from '@lfx/shared/shared.module';
import { HoverTriggerComponent } from '@lfx/shared/components/hover-trigger/hover-trigger.component';

@Component({
  selector: 'lfx-employee-info-card',
  imports: [CommonModule, SharedModule, HoverTriggerComponent],
  templateUrl: './employee-info-card.component.html',
  styleUrls: ['./employee-info-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployeeInfoCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() lfUserId: string;
  @Input() memberId: string;
  @Input() defaultEmployeeObject: DefaultEmployeeObject;
  @Input() organization: Company;
  @Input() otherEmails: string[];
  @Input() hasUsername = true;
  @Input() hasDashboardAccess = true;

  canMakeUpdates$ = of(false);
  isLFStaff$ = of(false);
  loading = false;
  errorMessage = '';
  employeeIncludesUnknownText = false;
  employeeEmailAddress = '';
  isValidEmailDomain = false;
  employeeDetails: EmployeeDetailsResponse;

  employeeDetailsSubscription = new Subscription();
  orgAffiliation: EmployeeOrgAffiliation[] = [];
  stint = {
    latest: '',
    past: [],
  };

  aliases = '';

  spinnerName = 'infoCardSpinner';
  isAllOrgsRollup = false;
  noDataPlaceholder = generalConstants.noDataPlaceholder;

  constructor(
    private userService: UserService,
    private companyService: CompanyService,
    private toastService: ToastService,
    private spinner: NgxSpinnerService
  ) {}

  get emailLink(): string {
    return this.employeeEmailAddress
      ? `mailto:${this.employeeEmailAddress}`
      : '';
  }

  readonly githubTooltipMessage =
    'User has not linked a GitHub account to their LFX profile.';

  get emailTooltipMessage(): string {
    if (!this.employeeEmailAddress) {
      return 'No email address found in user profile.';
    }
    if (this.employeeIncludesUnknownText) {
      return 'Email hidden because user identity is unknown.';
    }
    if (!this.isValidEmailDomain) {
      const emailDomain = this.employeeEmailAddress?.split('@')[1] || 'N/A';
      return `Email domain "${emailDomain}" does not match organization's registered domain aliases. Contact SFDC team to update domain aliases.`;
    }
    return '';
  }

  ngOnDestroy(): void {
    if (this.employeeDetailsSubscription) {
      this.employeeDetailsSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.isAllOrgsRollup = this.companyService.isAllOrgsRollup();
    this.isLFStaff$ = this.userService.isLFStaffUser();
    this.canMakeUpdates$ = this.userService
      .isCompanyAdminOrLFStaffUser()
      .pipe(
        map(isAdminOrStaffUser => isAdminOrStaffUser && !this.isAllOrgsRollup)
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lfUserId'] || changes['memberId'] || changes['organization']) {
      this.getEmployeeDetails();
    }

    if (changes['organization']) {
      const domainAlias = this.organization?.domainAlias || '';
      const link = this.organization?.link ? `,${this.organization.link}` : '';
      this.aliases = domainAlias + link;
    }

    if (changes['otherEmails']) {
      for (const email of this.otherEmails) {
        const emailDomain = email.split('@')[1];

        const isMatch = this.aliases.split(',').includes(emailDomain);

        if (isMatch) {
          this.isValidEmailDomain = isMatch;
          this.employeeEmailAddress = email;
          break;
        }
      }
    }
  }

  getEmployeeDetails() {
    if (!this.organization || !(this.lfUserId || this.memberId)) {
      return;
    }

    this.spinner.show(this.spinnerName);
    this.employeeDetailsSubscription = this.companyService
      .getOrgEmployeeDetails(
        this.organization.id,
        this.lfUserId || this.memberId,
        this.companyService.isAllOrgsRollup()
      )
      .subscribe(
        res => {
          this.spinner.hide(this.spinnerName);

          this.employeeEmailAddress =
            this.employeeDetails?.memberEmail ||
            this.defaultEmployeeObject?.email;

          const emailDomain = this.employeeEmailAddress?.split('@')[1];

          this.employeeDetails = res;
          this.stint = convertOrgAffiliationToStints(
            this.employeeDetails?.orgAffiliation || []
          );

          this.isValidEmailDomain = this.aliases
            .split(',')
            .includes(emailDomain);

          this.employeeIncludesUnknownText =
            this.employeeDetails?.memberDisplayName?.includes('[unknown]');
        },
        error => {
          this.spinner.hide(this.spinnerName);
          this.employeeDetails = null;
          this.employeeEmailAddress = '';
          this.employeeIncludesUnknownText = false;
          this.isValidEmailDomain = false;


          console.error('Error fetching employee details:', error);
        }
      );
  }

  inviteEmployee() {
    if (!this.employeeEmailAddress) {
      this.errorMessage = 'Invalid email address';
      this.loading = false;

      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.companyService
      .sendNewEmployeesInvite({
        organizationId: this.organization.id,
        invites: [
          {
            emailAddress: this.employeeEmailAddress,
            role: InviteRole.Employee,
          },
        ],
      })
      .subscribe(
        response => {
          this.loading = false;

          if (response && response.sendBulkInvite.success) {
            this.toastService.showSuccess('Your invite was sent to the recipient', {
              header: 'Invite Sent'
            });
          } else if (response && !response.sendBulkInvite.success) {
            if (
              response.sendBulkInvite.message ===
              'acs - username, role or scope conflict'
            ) {
              this.errorMessage =
                'The user you are inviting is already on the Employee List';
            } else {
              this.errorMessage = response.sendBulkInvite.message;
            }
          } else {
            this.errorMessage = 'Unknown error occurred';
          }
        },
        error => {
          this.loading = false;
          this.errorMessage = 'Failed to send invite';

          console.error('Error sending employee invite:', error);
        }
      );
  }

  formatGithubLink(githubId: string): string {
    if (!githubId.startsWith('https://github.com/')) {
      githubId = 'https://github.com/' + githubId;
    }

    return githubId;
  }

  formatDate(dateString: string): string {
    if (!dateString) {
      return dateString;
    }
    const splitedDate = dateString.split('-');

    return `${splitedDate[1]}/${splitedDate[2]}/${splitedDate[0]}`;
  }
}
